import { NullishRecord } from '@wyz/types';
import { isEmptyValue, parseFormData } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { NillishObject } from '~/common/types';
import { TYRE_SEARCH_DEFAULT_VALUES } from '~/purchase/helpers';
import { SEARCH_TYPE_ENUM, TYRE_SPEC_ENUM } from '~/purchase/schemas';
import {
  FilterFields,
  IBaseFilter,
  IBaseProductSearchQs,
  IBaseSearchFormSchema,
  IBaseTyreOffer,
  IProductListState,
  ISpecificityFilter,
} from '~/purchase/types';

type Args = NullishRecord<{
  identifier: string;
  width: number;
  height: number;
  diameter: number;
  loadIndex: number;
  speedIndex: string;
}>;

export const formatDimension = (values: Args) => {
  if (!isNil(values.identifier)) {
    return values.identifier;
  } else if (values.width && values.height && values.diameter) {
    return `${values.width}/${values.height} R${values.diameter} ${
      values.loadIndex || '-'
    }${values.speedIndex || '-'}`;
  } else {
    return undefined;
  }
};

type IFilters<T extends IBaseTyreOffer> = {
  [Key in FilterFields]: (
    products: T[],
    value: {
      filters: IProductListState['filters'];
      searchQuery: IProductListState['searchQuery'];
    },
    searchType?: (typeof SEARCH_TYPE_ENUM)[keyof typeof SEARCH_TYPE_ENUM],
  ) => T[];
};

const getSpecificityFilters = (): {
  [Key in ISpecificityFilter]: (tyreOffer: IBaseTyreOffer) => boolean;
} => ({
  electric: ({ tyre }) =>
    tyre.specification.electricId !== TYRE_SPEC_ENUM.ECO.DISABLED,
  eco: ({ tyre }) => tyre.specification.eco !== TYRE_SPEC_ENUM.ECO.DISABLED,
  sport: ({ tyre }) => tyre.specification.sportCompatibility,
  studdable: ({ tyre }) =>
    tyre.specification.typeTyre === 'complete wheel studded',
  studded: ({ tyre }) => tyre.specification.typeTyre === 'studded',
  reinforced: ({ tyre }) => !isNil(tyre.dimension.rfd),
  sealRunFlat: ({ tyre }) =>
    tyre.specification.seal === 'Y' || tyre.specification.runFlat,
  campingCar: ({ tyre }) => tyre.specification.campingCar === 1,
  promotion: (tyreOffer) =>
    tyreOffer.offers.some((offer) => offer.offerMetadata.promotion),
  pmsf: ({ tyre }) => tyre.season.pmsf === 'Y',
});

const getProductFilters = <T extends IBaseTyreOffer>(): IFilters<T> => ({
  segments: (products, { filters }) => {
    return filters.segments.length === 0
      ? products
      : products.filter(({ tyre }) =>
          filters.segments.includes(tyre.segment.publicKey),
        );
  },
  brands: (products, { filters }) =>
    filters.brands.length === 0
      ? products
      : products.filter(({ tyre }) => {
          return filters.brands.includes(tyre.brand.publicKey);
        }),
  seasons: (products, { filters }) =>
    filters.seasons.length === 0
      ? products
      : products.filter(({ tyre }) => {
          return filters.seasons.some((season) => tyre.season.id === season);
        }),
  vehicleTypes: (products, { filters }) =>
    filters.vehicleTypes.length === 0
      ? products
      : products.filter(({ tyre }) =>
          filters.vehicleTypes.includes(tyre.vehicleType.publicKey),
        ),
  homologations: (products, { filters }) => {
    if (filters.homologations.length === 0) {
      return products;
    }

    return products.filter(({ tyre }) => {
      const tyreHomologationBrands =
        isNil(tyre.homologations) || tyre.homologations.length === 0
          ? ['']
          : tyre.homologations.map(({ brand }) => brand);

      return tyreHomologationBrands.some((brand) =>
        filters.homologations.includes(brand),
      );
    });
  },
  availability: (products, { filters, searchQuery }, searchType) =>
    filters.availability.length === 0
      ? products
      : products.filter((tyreOffer) => {
          const requestedQuantity = searchQuery?.quantity ?? 0;
          const productQuantity = tyreOffer.totalAvailableQuantity;
          const hasCustomerStock =
            !isNil(tyreOffer.customerStock) && tyreOffer.customerStock > 0;
          const hasProximityStock =
            (tyreOffer.proximityStocks ?? []).length > 0;
          const isEmptyStock = tyreOffer.status === 'EMPTY_STOCK';
          const isTechnicalError = tyreOffer.status === 'TECHNICAL_ERROR';

          const conditions = {
            total: productQuantity >= requestedQuantity,
            partial: productQuantity > 0 && productQuantity < requestedQuantity,
            supplierStockUnavailable:
              (hasCustomerStock || hasProximityStock) &&
              (isEmptyStock || isTechnicalError),
            unavailable:
              (isEmptyStock && !hasCustomerStock && !hasProximityStock) ||
              (productQuantity === 0 &&
                (searchType === SEARCH_TYPE_ENUM.QUOTE
                  ? true
                  : !isTechnicalError && !hasCustomerStock)),
            error: isTechnicalError && !hasCustomerStock,
          };
          for (const availability of filters.availability) {
            if (conditions[availability]) {
              return true;
            }
          }
          return false;
        }),
  inStock: (products, { filters }) =>
    !filters.inStock
      ? products
      : products.filter(
          (tyreOffer) =>
            (!isNil(tyreOffer.customerStock) && tyreOffer.customerStock > 0) ||
            tyreOffer.proximityStocks.length > 0,
        ),
  proximityStock: (products, { filters }) =>
    !filters.proximityStock
      ? products
      : products.filter((tyreOffer) => tyreOffer.proximityStocks.length > 0),
  specificities: (products, { filters }) => {
    if (filters.specificities.length === 0) {
      return products;
    }
    let result = [...products];
    if (filters.specificities.includes('promotion')) {
      // remove suppliers that don't have a discount, so we can filter by suppliers length
      result = products.map((product) => ({
        ...product,
        offers: product.offers.filter(
          (offer) => offer.offerMetadata.discountPercentage !== 0,
        ),
      }));
    }
    return result.filter((product) => {
      for (const specificity of filters.specificities) {
        if (getSpecificityFilters()[specificity](product)) {
          return true;
        }
      }
      return false;
    });
  },
  j0: (products, { filters }) =>
    !filters.j0
      ? products
      : products.reduce<T[]>((result, product) => {
          const offers = product.offers.filter(
            (offer) => offer.offerMetadata.isJ0,
          );

          return offers.length > 0
            ? [...result, { ...product, offers }]
            : result;
        }, []),
  destocking: (products, { filters }) => {
    const showDestocking = filters.destocking.includes('destocking');
    const showNonDestocking = filters.destocking.includes('nonDestocking');

    if (!showDestocking && !showNonDestocking) {
      return products;
    }

    return products.filter((product) => {
      const isDestocking = product.offers.some(
        (offer) => offer.offerMetadata.isDestocking,
      );

      return (
        (showDestocking && isDestocking) || (showNonDestocking && !isDestocking)
      );
    });
  },
});

export function filterProducts<T extends IBaseTyreOffer = IBaseTyreOffer>(
  products: T[],
  listState: IProductListState,
  searchType?: (typeof SEARCH_TYPE_ENUM)[keyof typeof SEARCH_TYPE_ENUM],
) {
  let result = [...products];
  Object.values(getProductFilters<T>()).forEach((filterer) => {
    result = filterer(
      result,
      {
        filters: listState.filters,
        searchQuery: listState.searchQuery,
      },
      searchType,
    );
  });

  return result;
}

export const hasMinimalSearchParams = (
  values: IBaseSearchFormSchema,
): boolean => {
  return (
    !isEmptyValue(values.identifier?.value) ||
    (!isEmptyValue(values.width) &&
      !isEmptyValue(values.height) &&
      !isEmptyValue(values.diameter) &&
      !isEmptyValue(values.quantity))
  );
};

export function sortProducts(
  products: IBaseTyreOffer[],
  listState: IProductListState,
) {
  const result = [...products];
  const sort = listState.sort;
  if (!sort || sort === 'relevance') {
    return result;
  }

  let getters: Array<{
    getter: (product: IBaseTyreOffer) => number;
    asc: boolean;
  }> = [];

  if (sort === 'price') {
    getters = [
      {
        getter: (product) => {
          let result = Infinity;
          for (const offer of product.offers) {
            if (
              !!offer.offerMetadata.displayPrice &&
              !!offer.offerMetadata.availableQuantity
            ) {
              result = offer.offerMetadata.displayPrice;
              break;
            }
          }
          return result;
        },
        asc: true,
      },
      {
        getter: (product) =>
          product?.offers?.[0]?.offerMetadata.availableQuantity || 0,
        asc: false,
      },
      {
        getter: (product) =>
          product.tyreCreatedAt ? new Date(product.tyreCreatedAt).getTime() : 0,
        asc: false,
      },
    ];
  } else {
    getters = [
      {
        getter: (product) => {
          const date = product?.offers[0]?.offerMetadata.deliveryDate;
          return date ? new Date(date).getTime() : Infinity;
        },
        asc: true,
      },
    ];
  }

  return result.sort((product1, product2) => {
    for (const { getter, asc } of getters) {
      const diff = getter(product1) - getter(product2);
      if (diff !== 0 && !isNaN(diff)) {
        return asc ? diff : diff * -1;
      }
    }
    return 0;
  });
}

export const getTyreSearchDefaultValues = (
  overrides?: Partial<IBaseSearchFormSchema>,
) => ({
  ...TYRE_SEARCH_DEFAULT_VALUES,
  ...overrides,
});

export const parseSearchFromQs = (
  searchParams: URLSearchParams,
): Partial<IBaseSearchFormSchema> => {
  const parsedQueryParams = parseFormData<IBaseProductSearchQs>(
    searchParams.toString(),
  );

  const overrides: Partial<IBaseSearchFormSchema> = {};
  if (parsedQueryParams.identifier) {
    overrides.identifier = {
      label: decodeURIComponent(parsedQueryParams.identifier),
      value: decodeURIComponent(parsedQueryParams.identifier),
    };
    overrides.fieldNameSearch =
      parsedQueryParams.fieldNameSearch ?? 'DIMENSION';
  }
  (['width', 'height', 'diameter'] as const).forEach((field) => {
    if (!isEmptyValue(parsedQueryParams[field])) {
      overrides[field] = parsedQueryParams[field];
    }
  });
  if (parsedQueryParams.loadIndexes) {
    overrides.loadIndexes = parsedQueryParams.loadIndexes;
  }
  if (parsedQueryParams.speedIndexes) {
    overrides.speedIndexes = parsedQueryParams.speedIndexes;
  }
  if (parsedQueryParams.seasons) {
    overrides.seasons = parsedQueryParams.seasons;
  }
  if (parsedQueryParams.brands) {
    overrides.brands = Object.values(parsedQueryParams.brands);
  }
  overrides.quantity = parsedQueryParams.quantity ?? undefined;
  overrides.vehicleTypes = parsedQueryParams.vehicleTypes ?? [];
  overrides.includeHigherIndexes = Boolean(
    parsedQueryParams.includeHigherIndexes,
  );

  return overrides;
};

export const serializeSearchToQs = (
  values?: NillishObject<IBaseSearchFormSchema>,
): NillishObject<IBaseProductSearchQs> => {
  if (!values) {
    return {};
  }
  const { identifier, fieldNameSearch, ...rest } = values;
  const searchParams: NillishObject<IBaseProductSearchQs> = {
    ...rest,
  };
  if (identifier) {
    searchParams.identifier = encodeURIComponent(identifier.value);
    searchParams.fieldNameSearch = fieldNameSearch;
  }
  return searchParams;
};

const DEFAULT_AVAILABILITY: IBaseFilter['availability'] = [
  'total',
  'partial',
  'supplierStockUnavailable',
];
const DEFAULT_FILTERS: IBaseFilter = {
  segments: [],
  brands: [],
  seasons: [],
  vehicleTypes: [],
  homologations: [],
  availability: DEFAULT_AVAILABILITY,
  inStock: false,
  proximityStock: false,
  specificities: [],
  j0: false,
  destocking: [],
};

export const DEFAULT_SEARCH_SORT = 'relevance';
export const DEFAULT_PRODUCT_LIST_STATE: IProductListState = {
  searchQuery: undefined,
  filters: DEFAULT_FILTERS,
  sort: DEFAULT_SEARCH_SORT,
};

export const getInitialSearchMode = (
  searchQuery?: IBaseSearchFormSchema,
): 'fast' | 'manual' => {
  if (!searchQuery) {
    return 'fast';
  }

  if (!isEmptyValue(searchQuery.identifier?.value)) {
    return 'fast';
  }

  if (
    !isEmptyValue(searchQuery.width) &&
    !isEmptyValue(searchQuery.height) &&
    !isEmptyValue(searchQuery.diameter)
  ) {
    return 'manual';
  }

  return 'fast';
};

export const getFilterCount = <T extends FilterFields = FilterFields>(
  products: IBaseTyreOffer[],
  listState: IProductListState,

  filter: T,
  value: IBaseFilter[T],
): number => {
  return filterProducts(products, {
    ...listState,
    filters: {
      ...listState.filters,
      [filter]: value,
    },
  }).length;
};
